import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@shopify/polaris';
import {
  AlertTriangleIcon, CalendarIcon, ChevronDownIcon, ChevronRightIcon, PrintIcon, RefreshIcon, SearchIcon, XSmallIcon,
} from '@shopify/polaris-icons';
import { Ic, StatusPill } from './ui';

const API_BASE_URL = window.location.origin;

const authenticatedFetch = async (url, options = {}) => {
  const token = localStorage.getItem('access_token');
  if (!token) throw new Error('NO_TOKEN');
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  };
  const response = await fetch(url, config);
  if (response.status === 401) throw new Error('AUTH_FAILED');
  return response;
};

function ProductImage({ imageUrl, productTitle }) {
  const [imageLoaded, setImageLoaded] = useState(true);
  useEffect(() => { setImageLoaded(true); }, [imageUrl]);
  if (!imageUrl || !imageLoaded) {
    return <div className="uh-tr-img initials" aria-hidden="true">{(productTitle || 'PR').substring(0, 2).toUpperCase()}</div>;
  }
  return (
    <div className="uh-tr-img">
      <img src={imageUrl} alt={productTitle} onError={() => setImageLoaded(false)} />
    </div>
  );
}

function formatDate(d) {
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Print card — plain HTML, no Polaris ──────────────────────────────────────
const BIN_LOCATION_LABELS = {
  'lancaster':    'Teams Lancaster',
  'tinton-falls': 'Tinton Falls Warehouse',
  'brookfield':   'Teams Brookfield',
  'carrollton':   'Teams Carrollton',
};

function TransferPrintCard({ transfer, binLocation }) {
  const locationLabel = BIN_LOCATION_LABELS[binLocation] || binLocation;

  // Sort items by location_bin
  const items = (transfer.line_items?.edges || []).slice().sort((a, b) => {
    const la = (a.location_bin || '').toLowerCase();
    const lb = (b.location_bin || '').toLowerCase();
    return la < lb ? -1 : la > lb ? 1 : 0;
  });

  // Timeline: up to 7 events sorted descending
  const events = [...(transfer.timeline_events || [])]
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    .slice(0, 7);

  const statusColors = {
    'ready_to_ship': { bg: '#e3f1df', color: '#186a17' },
    'in_transit':    { bg: '#fdf4e7', color: '#7d5500' },
    'received':      { bg: '#e3f1df', color: '#186a17' },
    'cancelled':     { bg: '#fde8e8', color: '#c0392b' },
  };
  const statusKey = (transfer.status || '').toLowerCase().replace(/ /g, '_');
  const statusStyle = statusColors[statusKey] || { bg: '#e8f0fe', color: '#1a56db' };
  const statusLabel = (transfer.status || 'Unknown').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const fmtAddrLines = (loc) => {
    if (!loc) return [];
    const addr = loc.address || {};
    const line1 = addr.address1 || '';
    const line2 = [addr.city, addr.provinceCode].filter(Boolean).join(', ') + (addr.zip ? ' ' + addr.zip : '');
    return [line1, line2].filter(Boolean);
  };

  const now = new Date();
  const footerTs = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', paddingBottom: 32 }}>

      {/* ── Header ── */}
      <div style={{ pageBreakInside: 'avoid', breakInside: 'avoid', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#202223', lineHeight: 1.2 }}>
              Transfer {transfer.name}
            </div>
            <div style={{ fontSize: 12, color: '#6d7175', marginTop: 3 }}>Soccer Post</div>
          </div>
          <span style={{
            backgroundColor: statusStyle.bg, color: statusStyle.color,
            borderRadius: 20, padding: '4px 14px', fontSize: 13, fontWeight: 600,
            display: 'inline-block', marginTop: 4, whiteSpace: 'nowrap',
          }}>
            {statusLabel}
          </span>
        </div>
        <hr style={{ border: 'none', borderTop: '2px solid #303030', margin: '0 0 16px' }} />
      </div>

      {/* ── Origin / Destination ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, pageBreakInside: 'avoid', breakInside: 'avoid' }}>
        {[
          { label: 'FROM', loc: transfer.origin?.location },
          { label: 'TO',   loc: transfer.destination?.location },
        ].map(({ label, loc }) => (
          <div key={label} style={{
            flex: 1, border: '1px solid #e1e3e5', borderRadius: 8,
            padding: '10px 14px', backgroundColor: '#fafafa',
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6d7175', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#202223' }}>{loc?.name || '—'}</div>
            {fmtAddrLines(loc).map((line, i) => (
              <div key={i} style={{ fontSize: 12, color: '#6d7175', marginTop: 1 }}>{line}</div>
            ))}
          </div>
        ))}
      </div>

      {/* ── Transfer Details ── */}
      <div style={{
        border: '1px solid #e1e3e5', borderRadius: 8, padding: '10px 14px',
        marginBottom: 12, backgroundColor: '#fafafa',
        pageBreakInside: 'avoid', breakInside: 'avoid',
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#6d7175', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Transfer Details</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 28px', fontSize: 13 }}>
          <span><strong>Created:</strong> {(transfer.dateCreated || '').slice(0, 10) || '—'}</span>
          {transfer.referenceName && <span><strong>Reference:</strong> {transfer.referenceName}</span>}
          <span><strong>Total Qty:</strong> {transfer.totalQuantity ?? '—'}</span>
          {transfer.receivedQuantity != null && <span><strong>Received:</strong> {transfer.receivedQuantity}</span>}
          <span><strong>Bin Location:</strong> {locationLabel}</span>
        </div>
      </div>

      {/* ── Notes ── */}
      {transfer.note && (
        <div style={{
          border: '1px solid #e1e3e5', borderRadius: 8, padding: '10px 14px',
          marginBottom: 12, backgroundColor: '#fafafa',
          pageBreakInside: 'avoid', breakInside: 'avoid',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6d7175', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Notes</div>
          <div style={{ fontSize: 13, color: '#202223', whiteSpace: 'pre-wrap' }}>{transfer.note}</div>
        </div>
      )}

      {/* ── Timeline ── */}
      {events.length > 0 && (
        <div style={{
          border: '1px solid #e1e3e5', borderRadius: 8, padding: '10px 14px',
          marginBottom: 14, backgroundColor: '#fafafa',
          pageBreakInside: 'avoid', breakInside: 'avoid',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6d7175', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Timeline</div>
          {events.map((ev, i) => {
            const d = ev.createdAt ? new Date(ev.createdAt) : null;
            const dateStr = d
              ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' +
                d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
              : '';
            return (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: i < events.length - 1 ? 5 : 0 }}>
                <div style={{ fontSize: 11, color: '#6d7175', whiteSpace: 'nowrap', minWidth: 135 }}>{dateStr}</div>
                <div style={{ fontSize: 12, color: '#202223' }}>{ev.message || ev.body || ''}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Products Table ── */}
      {items.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ backgroundColor: '#f6f6f7' }}>
              <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #e1e3e5', width: 44 }}></th>
              <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #e1e3e5', fontWeight: 600 }}>Product</th>
              <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #e1e3e5', fontWeight: 600, width: '18%' }}>SKU</th>
              <th style={{ padding: '7px 8px', textAlign: 'left', borderBottom: '2px solid #e1e3e5', fontWeight: 600, width: '16%' }}>Bin</th>
              <th style={{ padding: '7px 8px', textAlign: 'center', borderBottom: '2px solid #e1e3e5', fontWeight: 600, width: 44 }}>Qty</th>
              <th style={{ padding: '7px 8px', textAlign: 'center', borderBottom: '2px solid #e1e3e5', fontWeight: 600, width: 52 }}>Picked</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => {
              const lineItem = item.node || item;
              const variant = lineItem.inventoryItem?.variant;
              const product = variant?.product;
              const variantTitle = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
              const imageUrl = item.image_url;
              return (
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '5px 8px', verticalAlign: 'middle' }}>
                    {imageUrl
                      ? <img src={imageUrl} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, border: '1px solid #e1e3e5', display: 'block' }} />
                      : <div style={{ width: 36, height: 36, borderRadius: 4, border: '1px solid #e1e3e5', backgroundColor: '#f6f6f7' }} />
                    }
                  </td>
                  <td style={{ padding: '5px 8px', verticalAlign: 'middle' }}>
                    <div style={{ fontWeight: 500, color: '#202223', lineHeight: 1.3 }}>{product?.title || 'Unknown'}</div>
                    {variantTitle && <div style={{ color: '#6d7175', fontSize: 11, marginTop: 1 }}>{variantTitle}</div>}
                  </td>
                  <td style={{ padding: '5px 8px', fontFamily: 'monospace', fontSize: 11, verticalAlign: 'middle' }}>
                    {lineItem.inventoryItem?.sku || '—'}
                  </td>
                  <td style={{ padding: '5px 8px', fontSize: 11, verticalAlign: 'middle', color: item.location_bin ? '#005bd3' : '#aaa', fontWeight: item.location_bin ? 500 : 400 }}>
                    {item.location_bin || '—'}
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 700, verticalAlign: 'middle' }}>
                    {item.quantity ?? lineItem.totalQuantity ?? '—'}
                  </td>
                  <td style={{ padding: '5px 8px', textAlign: 'center', verticalAlign: 'middle' }}>
                    <div style={{ width: 18, height: 18, border: '1.5px solid #303030', borderRadius: 3, display: 'inline-block' }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p style={{ color: '#999', fontSize: 13 }}>No line items found.</p>
      )}

      {/* ── Footer ── */}
      <div style={{ marginTop: 20, paddingTop: 8, borderTop: '1px solid #e1e3e5', display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6d7175' }}>
        <span>Generated {footerTs}</span>
        <span>{locationLabel}</span>
      </div>

    </div>
  );
}

function TransferReports() {
  const navigate = useNavigate();

  // ── Transfer list state ──
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [cursors, setCursors] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [endCursor, setEndCursor] = useState(null);

  // ── Filter state ──
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [originFilter, setOriginFilter] = useState('');
  const [destFilter, setDestFilter] = useState('');
  const [appliedLocs, setAppliedLocs] = useState({ origin: '', dest: '' });
  const [knownLocations, setKnownLocations] = useState([]);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [{ month, year }, setDateNav] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });
  const [pendingDates, setPendingDates] = useState({ start: null, end: null });

  // ── Generate / select state ──
  const [selectedTransfers, setSelectedTransfers] = useState([]);
  const [binLocation, setBinLocation] = useState('lancaster');

  // ── Print state ──
  const [printReadyTransfers, setPrintReadyTransfers] = useState(null);
  const [printFetching, setPrintFetching] = useState(false);
  const printPopupRef  = useRef(null);
  const printSourceRef = useRef(null);

  // ── Right sidebar state ──
  const [endpointResults, setEndpointResults] = useState([]);
  const [testingEndpoints, setTestingEndpoints] = useState(false);
  const [debugResults, setDebugResults] = useState([]);
  const [runningDebug, setRunningDebug] = useState(false);

  // ── Modal state ──
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferDetails, setTransferDetails] = useState(null);

  // ── Notice (errors) ──
  const [notice, setNotice] = useState('');
  const [diagOpen, setDiagOpen] = useState(false);
  const detailsRef = useRef(null);
  const datePopRef = useRef(null);
  const tbarRef = useRef(null);
  const [tbarHeight, setTbarHeight] = useState(0);
  useEffect(() => {
    if (!tbarRef.current) return undefined;
    const ro = new ResizeObserver(() => setTbarHeight(tbarRef.current?.offsetHeight || 0));
    ro.observe(tbarRef.current);
    return () => ro.disconnect();
  }, []);

  const binLocationOptions = [
    { label: 'Teams Lancaster', value: 'lancaster' },
    { label: 'Tinton Falls Warehouse', value: 'tinton-falls' },
    { label: 'Teams Brookfield', value: 'brookfield' },
    { label: 'Teams Carrollton', value: 'carrollton' },
  ];

  // ── Fetch a specific page ──
  const fetchPage = useCallback(async ({ cursor, search, fromDate, toDate, origin, destination }) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cursor) params.set('after', cursor);
      if (search) params.set('search', search);
      if (fromDate) params.set('date_from', fromDate.toISOString().split('T')[0]);
      if (toDate) params.set('date_to', toDate.toISOString().split('T')[0]);
      if (origin) params.set('origin', origin);
      if (destination) params.set('destination', destination);

      const response = await authenticatedFetch(`${API_BASE_URL}/api/transfers?${params}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setTransfers(data.transfers || []);
      setNotice('');
      setKnownLocations((prev) => {
        const names = new Set(prev);
        (data.transfers || []).forEach((t) => [t.origin?.location?.name, t.destination?.location?.name].forEach((n) => n && names.add(n)));
        return [...names].sort();
      });
      setHasNextPage(data.hasNextPage || false);
      setEndCursor(data.endCursor || null);
      return data.endCursor;
    } catch (err) {
      if (err.message === 'NO_TOKEN' || err.message === 'AUTH_FAILED') { navigate('/login'); return; }
      setNotice(`Failed to load transfers: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPage({ cursor: null, search: '', fromDate: null, toDate: null });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = useCallback(async () => {
    const from = dateFrom;
    const to = dateTo;
    const search = searchInput.trim();
    setAppliedSearch(search);
    setAppliedLocs({ origin: originFilter, dest: destFilter });
    setCursors([null]);
    setPageIndex(0);
    setEndCursor(null);
    await fetchPage({ cursor: null, search, fromDate: from, toDate: to, origin: originFilter, destination: destFilter });
  }, [searchInput, dateFrom, dateTo, originFilter, destFilter, fetchPage]);

  const clearFilters = useCallback(() => {
    setSearchInput('');
    setAppliedSearch('');
    setDateFrom(null);
    setDateTo(null);
    setOriginFilter('');
    setDestFilter('');
    setAppliedLocs({ origin: '', dest: '' });
    setPendingDates({ start: null, end: null });
    setCursors([null]);
    setPageIndex(0);
    setEndCursor(null);
    fetchPage({ cursor: null, search: '', fromDate: null, toDate: null });
  }, [fetchPage]);

  const handleNextPage = useCallback(async () => {
    const newIndex = pageIndex + 1;
    let cursorForPage = cursors[newIndex];
    let newCursors = cursors;
    if (!cursorForPage) {
      newCursors = [...cursors, endCursor];
      setCursors(newCursors);
      cursorForPage = endCursor;
    }
    setPageIndex(newIndex);
    await fetchPage({ cursor: cursorForPage, search: appliedSearch, fromDate: dateFrom, toDate: dateTo, origin: appliedLocs.origin, destination: appliedLocs.dest });
  }, [pageIndex, cursors, endCursor, appliedSearch, dateFrom, dateTo, appliedLocs, fetchPage]);

  const handlePrevPage = useCallback(async () => {
    const newIndex = pageIndex - 1;
    setPageIndex(newIndex);
    await fetchPage({ cursor: cursors[newIndex], search: appliedSearch, fromDate: dateFrom, toDate: dateTo, origin: appliedLocs.origin, destination: appliedLocs.dest });
  }, [pageIndex, cursors, appliedSearch, dateFrom, dateTo, appliedLocs, fetchPage]);

  // ── Date picker ──
  const dateRangeLabel = (() => {
    if (!dateFrom && !dateTo) return 'All dates';
    if (dateFrom && dateTo) return `${formatDate(dateFrom)} – ${formatDate(dateTo)}`;
    if (dateFrom) return `From ${formatDate(dateFrom)}`;
    return `Until ${formatDate(dateTo)}`;
  })();

  const applyDateRange = () => {
    setDateFrom(pendingDates.start || null);
    setDateTo(pendingDates.end || null);
    setDatePickerVisible(false);
  };

  const clearDateRange = () => {
    setPendingDates({ start: null, end: null });
    setDateFrom(null);
    setDateTo(null);
    setDatePickerVisible(false);
  };

  // ── Print — write to popup when data is ready ──
  useEffect(() => {
    if (!printReadyTransfers || !printSourceRef.current || !printPopupRef.current) return;
    const popup = printPopupRef.current;
    if (popup.closed) { setPrintReadyTransfers(null); return; }

    const content = printSourceRef.current.innerHTML;

    popup.document.open();
    popup.document.write(`<!DOCTYPE html><html>
<head><meta charset="utf-8"><title>Soccer Post Transfers</title>
<style>
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    margin: 0 auto;
    background: white;
    color: #202223;
    max-width: 760px;
    padding: 24px 32px;
  }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }
  table { border-collapse: collapse; width: 100%; }
  thead { display: table-header-group; }
  img { max-width: 100%; }
  @media print { @page { margin: 0.6in; } }
  .transfer-break { page-break-before: always; break-before: page; }
</style>
</head>
<body>
${content}
<script>
  setTimeout(function() { window.focus(); window.print(); }, 400);
<\/script>
</body></html>`);
    popup.document.close();

    setPrintReadyTransfers(null);
    printPopupRef.current = null;
  }, [printReadyTransfers]);

  // ── Print — fetch details then trigger popup render ──
  const executePrint = async (names) => {
    if (!names || names.length === 0) {
      setNotice('Please select at least one transfer');
      return;
    }

    // Open popup immediately (sync, avoids popup blockers)
    const popup = window.open('', '', 'width=920,height=700,scrollbars=yes,resizable=yes');
    if (!popup) {
      alert('Please allow popups for this site to enable printing.');
      return;
    }
    popup.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Loading…</title></head>
<body style="font-family:sans-serif;padding:40px;color:#374151">
  <p style="font-size:15px">Loading transfer details…</p>
</body></html>`);
    printPopupRef.current = popup;
    setPrintFetching(true);

    try {
      const details = await Promise.all(
        names.map(async (name) => {
          const strategies = [
            encodeURIComponent(name),
            name.replace('#', '%23'),
            name.replace('#', ''),
            name,
          ];
          for (const encoded of strategies) {
            try {
              const res = await authenticatedFetch(`${API_BASE_URL}/api/transfers/${encoded}?location=${binLocation}&print=1`);
              if (res && res.ok) {
                const data = await res.json();
                if (data.success && data.transfer) return data.transfer;
              }
            } catch {}
          }
          return null;
        })
      );
      setPrintFetching(false);
      const valid = details.filter(Boolean);
      if (valid.length === 0) {
        popup.close();
        printPopupRef.current = null;
        setNotice('Could not load transfer details for printing.');
        return;
      }
      setPrintReadyTransfers(valid); // triggers useEffect → writes to popup
    } catch (err) {
      setPrintFetching(false);
      if (!popup.closed) popup.close();
      printPopupRef.current = null;
      setNotice(`Print failed: ${err.message}`);
    }
  };

  const handleGenerate = (names) => {
    executePrint(names);
  };

  // ── Transfer details modal ──
  const viewTransferDetails = async (transferName) => {
    setShowTransferModal(true);
    setTransferDetails(null);
    try {
      const strategies = [
        encodeURIComponent(transferName),
        transferName.replace('#', '%23'),
        transferName.replace('#', ''),
        transferName,
      ];
      for (const encoded of strategies) {
        try {
          const response = await authenticatedFetch(`${API_BASE_URL}/api/transfers/${encoded}?location=${binLocation}`);
          if (response && response.ok) {
            const data = await response.json();
            if (data.success && data.transfer) {
              setTransferDetails(data.transfer);
              return;
            }
          }
        } catch {}
      }
      setNotice(`Transfer ${transferName} not found`);
      setShowTransferModal(false);
    } catch (err) {
      if (err.message === 'NO_TOKEN') { navigate('/login'); return; }
      setNotice('Failed to load transfer details');
      setShowTransferModal(false);
    }
  };

  const toggleTransferSelection = (transferName) => {
    setSelectedTransfers(prev =>
      prev.includes(transferName) ? prev.filter(t => t !== transferName) : [...prev, transferName]
    );
  };

  // ── Endpoint / auth debug ──
  const testEndpoints = async () => {
    setTestingEndpoints(true);
    setEndpointResults([]);
    const endpoints = [
      { name: 'Health', url: `${API_BASE_URL}/api/health`, needsAuth: true },
      { name: 'Auth Verify', url: `${API_BASE_URL}/api/auth/verify`, needsAuth: true },
      { name: 'Transfers', url: `${API_BASE_URL}/api/transfers`, needsAuth: true },
    ];
    const results = [];
    for (const ep of endpoints) {
      try {
        const fetchFn = ep.needsAuth ? authenticatedFetch : fetch;
        const r = await fetchFn(ep.url);
        results.push({ name: ep.name, ok: r.ok, status: r.status });
      } catch (err) {
        results.push({ name: ep.name, ok: false, status: err.message });
      }
    }
    setEndpointResults(results);
    setTestingEndpoints(false);
  };

  const runAuthDebug = async () => {
    setRunningDebug(true);
    setDebugResults([]);
    const token = localStorage.getItem('access_token');
    const results = [];
    results.push({
      test: 'Token Storage',
      status: token ? 'PASS' : 'FAIL',
      details: token ? `Length: ${token.length}, Preview: ${token.substring(0, 30)}...` : 'No token in localStorage',
    });
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp;
        const now = Math.floor(Date.now() / 1000);
        results.push({
          test: 'Token Expiry',
          status: exp > now ? 'PASS' : 'FAIL',
          details: `Expires: ${new Date(exp * 1000).toISOString()}, Valid: ${exp > now}`,
        });
      } catch (e) {
        results.push({ test: 'Token Parse', status: 'FAIL', details: e.message });
      }
      for (const ep of [
        { name: 'Health Check', url: `${API_BASE_URL}/api/health`, needsAuth: true },
        { name: 'Auth Verify', url: `${API_BASE_URL}/api/auth/verify`, needsAuth: true },
        { name: 'Transfers List', url: `${API_BASE_URL}/api/transfers`, needsAuth: true },
      ]) {
        try {
          const r = await (ep.needsAuth ? authenticatedFetch : fetch)(ep.url);
          results.push({ test: ep.name, status: r.ok ? 'PASS' : 'FAIL', details: `Status: ${r.status}` });
        } catch (err) {
          results.push({ test: ep.name, status: 'FAIL', details: err.message });
        }
      }
    }
    setDebugResults(results);
    setRunningDebug(false);
  };

  const getStatusTone = (status) => {
    switch (status?.toLowerCase()) {
      case 'ready_to_ship': case 'ready to ship': return 'success';
      case 'in_transit': case 'in transit': return 'attention';
      case 'cancelled': return 'critical';
      default: return 'info';
    }
  };

  const hasActiveFilters = !!(appliedSearch || dateFrom || dateTo || appliedLocs.origin || appliedLocs.dest);
  const currentPage = pageIndex + 1;
  const isBusy = loading || printFetching;
  const locationOptions = [...new Set([...knownLocations, ...Object.values(BIN_LOCATION_LABELS)])].sort();
  const count = (status) => transfers.filter((t) => (t.status || '').toLowerCase().replace(/ /g, '_') === status).length;
  const allOnPageSelected = transfers.length > 0 && transfers.every((t) => selectedTransfers.includes(t.name));
  const toggleAllOnPage = () => setSelectedTransfers((prev) => (allOnPageSelected
    ? prev.filter((n) => !transfers.some((t) => t.name === n))
    : [...new Set([...prev, ...transfers.map((t) => t.name)])]));
  const statusTone = (status) => ({ success: 'ful', attention: 'hold', critical: 'can', info: 'par' }[getStatusTone(status)]);
  const statusLabel = (status) => (status || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  const PRESETS = [['All dates', null], ['Last 7 days', 7], ['Last 30 days', 30], ['Last 90 days', 90]];
  const pickPreset = (days) => {
    const to = new Date();
    const from = days ? new Date(Date.now() - days * 86400000) : null;
    setPendingDates({ start: from, end: days ? to : null });
    setDateFrom(from);
    setDateTo(days ? to : null);
    setDatePickerVisible(false);
  };

  useEffect(() => {
    const d = detailsRef.current;
    if (!d) return;
    if (showTransferModal && !d.open) d.showModal();
    if (!showTransferModal && d.open) d.close();
  }, [showTransferModal]);
  useEffect(() => {
    if (!datePickerVisible) return undefined;
    const close = (e) => { if (datePopRef.current && !datePopRef.current.contains(e.target)) setDatePickerVisible(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [datePickerVisible]);

  const detailName = transferDetails?.name;
  const detailSelected = detailName && selectedTransfers.includes(detailName);

  return (
    <>
      {/* Hidden render div — React renders TransferPrintCard here so we can grab innerHTML */}
      {printReadyTransfers && (
        <div
          ref={printSourceRef}
          style={{ position: 'fixed', left: -10000, top: 0, width: 760, visibility: 'hidden', pointerEvents: 'none', overflow: 'visible' }}
          aria-hidden="true"
        >
          {printReadyTransfers.map((transfer, idx) => (
            <React.Fragment key={transfer.name || idx}>
              {idx > 0 && <div className="transfer-break" />}
              <TransferPrintCard transfer={transfer} binLocation={binLocation} />
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="uh uh-page">
        <div className="uh-ph">
          <div>
            <h1 className="uh-h1">Shopify Transfers</h1>
            <div className="uh-sub">Find transfers, tick the ones to move, and print their sheets for a bin location.</div>
          </div>
          <div className="uh-statcard" aria-label="This page of results">
            <div className="stats">
              <div className="uh-stat"><span className="v">{transfers.length}</span><span className="k">On this page</span></div>
              <div className="uh-stat"><span className="v">{count('ready_to_ship')}</span><span className="k">Ready to ship</span></div>
              <div className="uh-stat"><span className="v">{count('in_transit')}</span><span className="k">In transit</span></div>
              <div className="uh-stat"><span className="v">{selectedTransfers.length}</span><span className="k">Selected</span></div>
            </div>
          </div>
        </div>

        {notice && (
          <div className="uh-alert crit">
            <Ic source={AlertTriangleIcon} sm /><div>{notice}</div>
            <button type="button" className="uh-ibtn" aria-label="Dismiss" style={{ marginLeft: 'auto' }} onClick={() => setNotice('')}><Ic source={XSmallIcon} sm /></button>
          </div>
        )}

        <div className="uh-tr-grid" style={{ '--tr-tbar-h': `${tbarHeight}px` }}>
          <div className="uh-card">
            <div className="uh-tr-tbar" ref={tbarRef}>
              <div className="uh-daterange" ref={datePopRef}>
                <button type="button" className="uh-btn" aria-expanded={datePickerVisible} onClick={() => setDatePickerVisible((v) => !v)}>
                  <Ic source={CalendarIcon} sm />{dateRangeLabel}<Ic source={ChevronDownIcon} sm />
                </button>
                {datePickerVisible && (
                  <div className="uh-daterange-pop">
                    <div className="presets">
                      {PRESETS.map(([label, days]) => <button key={label} type="button" onClick={() => pickPreset(days)}>{label}</button>)}
                    </div>
                    <div className="cal">
                      <DatePicker
                        month={month}
                        year={year}
                        onChange={(range) => setPendingDates(range)}
                        onMonthChange={(m, y) => setDateNav({ month: m, year: y })}
                        selected={pendingDates.start ? pendingDates : undefined}
                        allowRange
                        disableDatesAfter={new Date()}
                      />
                      <div className="acts">
                        <button type="button" className="uh-btn slim" onClick={clearDateRange}>Clear</button>
                        <button type="button" className="uh-btn primary slim" onClick={applyDateRange}>Apply</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <select className="uh-select" aria-label="Origin location" value={originFilter} onChange={(e) => setOriginFilter(e.target.value)}>
                <option value="">From: all locations</option>
                {locationOptions.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <select className="uh-select" aria-label="Destination location" value={destFilter} onChange={(e) => setDestFilter(e.target.value)}>
                <option value="">To: all locations</option>
                {locationOptions.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <div className="uh-search">
                <Ic source={SearchIcon} sm />
                <input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyFilters(); }}
                  placeholder="Transfer #, e.g. T0115"
                  aria-label="Transfer number"
                />
              </div>
              <button type="button" className="uh-btn primary" onClick={applyFilters} disabled={loading}><Ic source={SearchIcon} sm />Search</button>
              {hasActiveFilters && <button type="button" className="uh-btn" onClick={clearFilters}>Clear</button>}
              <span className="uh-sp" />
              <span className="uh-help">{hasActiveFilters ? 'Filtered' : `Page ${currentPage}`} · {transfers.length} result{transfers.length !== 1 ? 's' : ''}</span>
              <button
                type="button" className="uh-link" disabled={loading}
                onClick={() => fetchPage({ cursor: cursors[pageIndex], search: appliedSearch, fromDate: dateFrom, toDate: dateTo, origin: appliedLocs.origin, destination: appliedLocs.dest })}
              >
                <Ic source={RefreshIcon} sm />Refresh
              </button>
            </div>

            <div className="uh-tscroll uh-tr-scroll">
              <table className="uh-tbl uh-tr-tbl">
                <thead>
                  <tr>
                    <th style={{ width: 34 }}>
                      <input type="checkbox" className="uh-tr-check" checked={allOnPageSelected} onChange={toggleAllOnPage} aria-label="Select all on this page" />
                    </th>
                    <th>Transfer #</th><th>Date</th><th>Origin → Destination</th><th>Status</th><th className="r">Items</th><th className="r">Qty</th><th />
                  </tr>
                </thead>
                <tbody>
                  {loading && transfers.length === 0 && <tr><td colSpan={8} className="uh-empty">Loading transfers…</td></tr>}
                  {!loading && transfers.length === 0 && <tr><td colSpan={8} className="uh-empty">No transfers found.</td></tr>}
                  {transfers.map((transfer) => {
                    const isSelected = selectedTransfers.includes(transfer.name);
                    return (
                      <tr key={transfer.id} className={isSelected ? 'uh-tr-sel' : ''}>
                        <td>
                          <input type="checkbox" className="uh-tr-check" checked={isSelected} onChange={() => toggleTransferSelection(transfer.name)} aria-label={`Select ${transfer.name}`} />
                        </td>
                        <td className="nm">{transfer.name}</td>
                        <td className="uh-muted uh-num uh-tr-date">{(transfer.dateCreated || '').slice(0, 10) || '—'}</td>
                        <td>
                          <span className="uh-tr-route">
                            <span className="loc">{transfer.origin?.location?.name || '—'}</span>→<span className="loc">{transfer.destination?.location?.name || '—'}</span>
                          </span>
                        </td>
                        <td><StatusPill tone={statusTone(transfer.status)}>{statusLabel(transfer.status)}</StatusPill></td>
                        <td className="r uh-num">{transfer.itemCount ?? '—'}</td>
                        <td className="r uh-num">{transfer.totalQuantity ?? '—'}</td>
                        <td className="r"><button type="button" className="uh-btn slim" onClick={() => viewTransferDetails(transfer.name)}>Details</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {hasActiveFilters && hasNextPage && transfers.length < 50 && (
              <div className="uh-tfoot"><span>Looked through the most recent transfers. Use Next to keep looking.</span></div>
            )}
            <div className="uh-tr-pager">
              <button type="button" className="uh-btn slim" disabled={pageIndex === 0 || loading} onClick={handlePrevPage}>← Previous</button>
              <span>Page {currentPage}</span>
              <button type="button" className="uh-btn slim" disabled={!hasNextPage || loading} onClick={handleNextPage}>Next →</button>
            </div>
          </div>

          <div className="uh-col uh-tr-side">
            <div className="uh-card">
              <div className="uh-card-h"><h2 className="uh-h2 lg">Print transfer sheets</h2></div>
              <div className="uh-card-b uh-tr-print">
                <div className="uh-fld">
                  <label htmlFor="tr-bin">Bin location</label>
                  <select id="tr-bin" className="uh-inp" value={binLocation} onChange={(e) => setBinLocation(e.target.value)}>
                    {binLocationOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <span className="uh-help">Printed sheets show the bins for this location.</span>
                </div>
                <div>
                  <div className="uh-tr-selhead">
                    <h3 className="uh-h2">Selected</h3><span className="uh-tcount">{selectedTransfers.length}</span><span className="uh-sp" />
                    {selectedTransfers.length > 0 && <button type="button" className="uh-link" onClick={() => setSelectedTransfers([])}>Clear selection</button>}
                  </div>
                  {selectedTransfers.length > 0 ? (
                    <div className="uh-tr-chips">
                      {selectedTransfers.map((name) => (
                        <span key={name} className="chip">{name}
                          <button type="button" aria-label={`Remove ${name}`} onClick={() => toggleTransferSelection(name)}>×</button>
                        </span>
                      ))}
                    </div>
                  ) : <div className="uh-help">Nothing selected. Tick transfers in the list.</div>}
                </div>
                <button type="button" className="uh-btn primary wide" disabled={selectedTransfers.length === 0 || isBusy} onClick={() => handleGenerate(selectedTransfers)}>
                  <Ic source={PrintIcon} sm />
                  {isBusy && selectedTransfers.length > 0 ? 'Preparing…' : selectedTransfers.length ? `Print ${selectedTransfers.length} sheet${selectedTransfers.length > 1 ? 's' : ''}` : 'Print'}
                </button>
              </div>
            </div>

            <div className="uh-card">
              <button type="button" className="uh-tr-acc" aria-expanded={diagOpen} onClick={() => setDiagOpen((v) => !v)}>
                <Ic source={diagOpen ? ChevronDownIcon : ChevronRightIcon} sm />Diagnostics<span className="uh-sp" /><span className="uh-help">Connection checks</span>
              </button>
              {diagOpen && (
                <div className="uh-tr-diag">
                  <div className="r">
                    <button type="button" className="uh-btn slim" onClick={testEndpoints} disabled={testingEndpoints}>{testingEndpoints ? 'Testing…' : 'Test endpoints'}</button>
                    <span className="uh-sp" />
                    {endpointResults.map((r) => <StatusPill key={r.name} tone={r.ok ? 'ful' : 'can'}>{r.name} {r.ok ? 'OK' : 'failed'}</StatusPill>)}
                  </div>
                  <div className="r">
                    <button type="button" className="uh-btn slim" onClick={runAuthDebug} disabled={runningDebug}>{runningDebug ? 'Checking…' : 'Check sign-in token'}</button>
                  </div>
                  {debugResults.map((r) => (
                    <div key={r.test} className="d">
                      <StatusPill tone={r.status === 'PASS' ? 'ful' : 'can'}>{r.test}</StatusPill>
                      <span className="uh-help">{r.details}</span>
                    </div>
                  ))}
                  <div className="uh-help">Health, sign-in and transfer list checks against the Hub's API. For when the list won't load.</div>
                </div>
              )}
            </div>
          </div>
        </div>

        <dialog className="uh-dlg uh-tr-dlg" ref={detailsRef} aria-labelledby="tr-dlg-h" onCancel={() => setShowTransferModal(false)}>
          <div className="uh-dlg-h">
            <h2 id="tr-dlg-h">{detailName ? `Transfer ${detailName}` : 'Transfer details'}</h2>
            {transferDetails && <StatusPill tone={statusTone(transferDetails.status)}>{statusLabel(transferDetails.status)}</StatusPill>}
            <button type="button" className="uh-ibtn" aria-label="Close" onClick={() => setShowTransferModal(false)}><Ic source={XSmallIcon} /></button>
          </div>
          <div className="uh-dlg-b">
            {!transferDetails ? <span className="uh-muted">Loading transfer details…</span> : (
              <>
                <div className="uh-tr-meta">
                  <div><div className="k">Origin</div><b>{transferDetails.origin?.location?.name || 'N/A'}</b><div className="uh-help">{transferDetails.origin?.location?.address?.address1 || ''}</div></div>
                  <div><div className="k">Destination</div><b>{transferDetails.destination?.location?.name || 'N/A'}</b><div className="uh-help">{transferDetails.destination?.location?.address?.address1 || ''}</div></div>
                  <div><div className="k">Created</div>{(transferDetails.dateCreated || '').slice(0, 10) || 'N/A'}</div>
                  <div><div className="k">Total qty</div><b>{transferDetails.totalQuantity || 'N/A'}</b></div>
                </div>
                {transferDetails.line_items?.edges && (
                  <div>
                    <h3 className="uh-h2" style={{ marginBottom: 4 }}>Line items ({transferDetails.line_items.edges.length})</h3>
                    {transferDetails.line_items.edges.map((item) => {
                      const lineItem = item.node || item;
                      const product = lineItem.inventoryItem?.variant?.product;
                      return (
                        <div key={lineItem.id} className="uh-tr-li">
                          <ProductImage imageUrl={item.image_url || product?.featuredImage?.url} productTitle={product?.title || 'Product'} />
                          <div>
                            <div className="nm">{product?.title || 'Unknown Product'}</div>
                            <div className="uh-help uh-mono">SKU {lineItem.inventoryItem?.sku || 'N/A'}{item.location_bin ? ` · Bin ${item.location_bin} (${BIN_LOCATION_LABELS[binLocation] || binLocation})` : ''}</div>
                          </div>
                          <b className="uh-num">Qty {item.quantity || lineItem.totalQuantity || 'N/A'}</b>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="uh-dlg-f">
            <button type="button" className="uh-btn" onClick={() => setShowTransferModal(false)}>Close</button>
            {detailName && (
              <button type="button" className="uh-btn primary" onClick={() => { toggleTransferSelection(detailName); setShowTransferModal(false); }}>
                {detailSelected ? 'Remove from printing' : 'Select for printing'}
              </button>
            )}
          </div>
        </dialog>
      </div>
    </>
  );
}

export default TransferReports;
